import {
  Box,
  Button,
  Modal
} from '@platform/ui-library';
import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { LoadingSpinner, SelectControl } from '@app/form_components';
import { useServices } from '@app/hooks';
import {
  Option,
  SandboxResponse,
  TimeInMs
} from '@app/types';
import {
  copyToClipboard,
  stringify,
  wait
} from '@app/utilities';
import styles from './RunScriptDialog.module.scss';

type RunScriptDialogProps = {
  /** The current script content from the editor. */
  script: string;
  /** Function arguments defined on the node. */
  functionArguments: { argumentName: string }[];
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Name of the node being edited, used as the default function name. */
  nodeName: string;
  /** Callback to close the dialog. */
  onClose: () => void;
};

type Result = (
  {
    error?: never;
    response: SandboxResponse;
    title?: never;
  } | {
    error: ReactNode;
    response?: never;
    title?: ReactNode;
  }
);

type ScriptAnalysis = {
  functionName: string;
  moduleVariables: string[];
  parameterDefaults: Record<string, string>;
  parameterNames: string[];
  sessionVariables: string[];
};

/** Regex to find api.session().get('varName') or api.session.get('varName') */
const sessionGetRegex = /api\.session(?:\(\))?\.get\(\s*['"]([^'"]+)['"]\s*\)/g;

/** Regex to find api.module().get('varName') or api.module.get('varName') */
const moduleGetRegex = /api\.module(?:\(\))?\.get\(\s*['"]([^'"]+)['"]\s*\)/g;

/**
 * Analyze a script to extract function name, parameters, and variable references.
 * The script is always wrapped in a function declaration using the node name and
 * its configured arguments, so inner function declarations are never extracted.
 */
function analyzeScript(
  script: string,
  functionArguments: { argumentName: string }[],
  nodeName: string
): ScriptAnalysis {
  const functionName = nodeName || 'main';
  const parameterNames = functionArguments.map((a) => a.argumentName).filter(Boolean);

  const sessionVariables = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = sessionGetRegex.exec(script)) !== null) {
    sessionVariables.add(match[1]);
  }

  const moduleVariables = new Set<string>();
  while ((match = moduleGetRegex.exec(script)) !== null) {
    moduleVariables.add(match[1]);
  }

  return {
    functionName,
    moduleVariables: [...moduleVariables],
    parameterDefaults: {},
    parameterNames,
    sessionVariables: [...sessionVariables]
  };
}

/**
 * Build a name→value record from an array of names, optionally preserving
 * existing values from a previous record. Falls back to defaults when no
 * existing value is present.
 */
function namesToValues(
  names: string[],
  existing: Record<string, string> = {},
  defaults: Record<string, string> = {}
): Record<string, string> {
  const result: Record<string, string> = {};
  names.forEach((name) => {
    result[name] = existing[name] ?? defaults[name] ?? '';
  });
  return result;
}

export const RunScriptDialog: FC<RunScriptDialogProps> = ({
  functionArguments,
  isOpen,
  nodeName,
  onClose,
  script
}) => {
  const { errorMessageService, processEngineService } = useServices();
  const endpoints = window.dynamicConfig.processEngineEndpoints ?? [];
  const endpointOptions = useMemo<Option<string>[]>(
    () => endpoints.map((ep) => ({ label: ep, value: ep })),
    [endpoints]
  );

  const analysis = useMemo(
    () => analyzeScript(script, functionArguments, nodeName),
    [script, functionArguments, nodeName]
  );

  const [endpoint, setEndpoint] = useState(endpoints[0] ?? '');
  const [inputValues, setInputValues] = useState(
    () => namesToValues(analysis.parameterNames, {}, analysis.parameterDefaults)
  );
  const [sessionValues, setSessionValues] = useState(
    () => namesToValues(analysis.sessionVariables)
  );
  const [moduleValues, setModuleValues] = useState(
    () => namesToValues(analysis.moduleVariables)
  );
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setResult(null);
      containerRef.current?.focus();
    }
  }, [isOpen]);

  // When the script changes, new parameters / variables may appear. Merge them
  // into the existing values so that previously-entered values are preserved
  // while new fields get an empty default.
  useEffect(() => {
    setInputValues((prev) => namesToValues(analysis.parameterNames, prev, analysis.parameterDefaults));
    setSessionValues((prev) => namesToValues(analysis.sessionVariables, prev));
    setModuleValues((prev) => namesToValues(analysis.moduleVariables, prev));
  }, [analysis]);

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const buildRequestBody = useCallback(() => {
    const params = analysis.parameterNames.join(', ');
    const fullScript = `function ${analysis.functionName}(${params}) { ${script} }`;

    const tryParse = (value: string): unknown => {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const parseValues = (values: Record<string, string>): Record<string, unknown> => {
      return Object.fromEntries(Object.entries(values).map(([key, value]) => {
        return [key, tryParse(value)];
      }));
    };

    return {
      functionName: analysis.functionName,
      inputs: Object.values(inputValues).map(tryParse),
      moduleVariables: parseValues(moduleValues),
      script: fullScript,
      sessionVariables: parseValues(sessionValues)
    };
  }, [analysis, inputValues, moduleValues, script, sessionValues]);

  const handleRun = useCallback(async () => {
    setIsRunning(true);
    setResult(null);

    try {
      const response = await processEngineService.executeSandbox(
        buildRequestBody(),
        endpoint
      );

      setResult((response.success) ? { response } : (
        { error: response.error ?? 'Script execution failed' }
      ));
    } catch (error) {
      const { message, title } = await errorMessageService.createErrorMessage(
        error as Error | Response | string
      );
      setResult({ error: message, title });
    } finally {
      setIsRunning(false);
    }
  }, [buildRequestBody, endpoint, errorMessageService, processEngineService]);

  const handleCopyJson = useCallback(async () => {
    if (!result?.response) {
      return;
    }
    const json = stringify(result.response);
    await copyToClipboard(json);
    setCopiedJson(true);
    await wait(2 * TimeInMs.second);
    setCopiedJson(false);
  }, [result]);

  const handleCopyCurl = useCallback(async () => {
    const body = stringify(buildRequestBody());
    const escaped = body.replace(/'/g, '\'\\\'\'');
    const curl = `curl -X POST '${endpoint}/test/sandbox/execute' \\\n  -H 'Content-Type: application/json' \\\n  -d '${escaped}'`;
    await copyToClipboard(curl);
    setCopiedCurl(true);
    await wait(2 * TimeInMs.second);
    setCopiedCurl(false);
  }, [buildRequestBody, endpoint]);

  const hasVariables = (
    analysis.parameterNames.length > 0
    || analysis.sessionVariables.length > 0
    || analysis.moduleVariables.length > 0
  );

  return (
    <Modal
      className={styles.modalWrapper}
      handleClose={onClose}
      isOpen={isOpen}
    >
      <Box theme="noPadding">
        <div className="box flex-column header no-border">
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <i className="fe icon-play" />
            </div>
            <h2>Run Script</h2>
          </div>
        </div>

        <div className={styles.container} ref={containerRef} tabIndex={-1}>
          <div className={styles.disclaimer}>
            <i className={`fe icon-info ${styles.disclaimerIcon}`} />
            <span>
              This runs your script in a standalone GraalVM sandbox, not the full Process Engine
              runtime. Some platform APIs may behave differently. Use this for initial code
              validation only &mdash; full testing should still be done through the Process Engine.
            </span>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <i className={`fe icon-server ${styles.sectionIcon}`} />
              Endpoint
            </div>
            <SelectControl
              name="endpoint"
              onChange={(value) => setEndpoint(value ?? '')}
              options={endpointOptions}
              value={endpoint}
            />
          </div>

          <VariableSection
            icon="icon-sliders"
            names={analysis.parameterNames}
            onChange={setInputValues}
            title="Function Inputs"
            values={inputValues}
          />
          <VariableSection
            icon="icon-layers"
            names={analysis.sessionVariables}
            onChange={setSessionValues}
            title="Session Variables"
            values={sessionValues}
          />
          <VariableSection
            icon="icon-package"
            names={analysis.moduleVariables}
            onChange={setModuleValues}
            title="Module Variables"
            values={moduleValues}
          />

          {(hasVariables) ? null : (
            <div className={styles.section}>
              <p className={styles.emptyMessage}>
                No function parameters or variable references detected.
                The script will be executed as-is.
              </p>
            </div>
          )}

          {result && (() => {
            const isError = !!result.error;

            return (
              <div
                className={`${styles.section} ${(isError) ? styles.error : styles.success}`}
                ref={resultRef}
              >
                <div className={styles.sectionHeader}>
                  <i className={`fe icon-${(isError) ? 'alert-triangle' : 'check-circle'} ${styles.sectionIcon}`} />
                  Result
                  <span className={`${styles.statusBadge} ${(isError) ? styles.statusError : styles.statusSuccess}`}>
                    {(isError) ? (result.title ?? 'Error') : 'Success'}
                  </span>
                  {result.response && (
                    <span className={styles.executionTime}>
                      <i className={`fe icon-clock ${styles.executionTimeIcon}`} />
                      {result.response.executionTimeMs}ms
                    </span>
                  )}
                </div>

                {result.error && (
                  <div className={styles.errorMessage}>
                    {result.error}
                  </div>
                )}

                {result.response && (
                  <div className={styles.resultDetails}>
                    <div className={styles.resultRow}>
                      <div className={styles.resultRowHeader}>
                        <i className={`fe icon-corner-down-right ${styles.resultRowIcon}`} />
                        Return Value
                      </div>
                      <div className={styles.resultValue}>
                        {(result.response.returnValue == null) ? (
                          <span className={styles.nullValue}>null</span>
                        ) : (
                          (typeof result.response.returnValue === 'object') ? (
                            stringify(result.response.returnValue)
                          ) : String(result.response.returnValue)
                        )}
                      </div>
                    </div>

                    {result.response.logs.length > 0 && (
                      <div className={styles.resultRow}>
                        <div className={styles.resultRowHeader}>
                          <i className={`fe icon-file-text ${styles.resultRowIcon}`} />
                          Logs
                          <span className={styles.logCount}>{result.response.logs.length}</span>
                        </div>
                        <div className={styles.logList}>
                          {result.response.logs.map((log, i) => (
                            <div
                              className={`${styles.logEntry} ${styles[`log${log.level}`] ?? ''}`}
                              key={i}
                            >
                              <span className={styles.logLevel}>{log.level}</span>
                              <span className={styles.logMessage}>{log.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {Object.keys(result.response.sessionVariables).length > 0 && (
                      <div className={styles.resultRow}>
                        <div className={styles.resultRowHeader}>
                          <i className={`fe icon-layers ${styles.resultRowIcon}`} />
                          Session Variables (after execution)
                        </div>
                        <div className={styles.resultValue}>
                          {stringify(result.response.sessionVariables)}
                        </div>
                      </div>
                    )}

                    {Object.keys(result.response.moduleVariables).length > 0 && (
                      <div className={styles.resultRow}>
                        <div className={styles.resultRowHeader}>
                          <i className={`fe icon-package ${styles.resultRowIcon}`} />
                          Module Variables (after execution)
                        </div>
                        <div className={styles.resultValue}>
                          {stringify(result.response.moduleVariables)}
                        </div>
                      </div>
                    )}

                    <button
                      className={styles.copyJsonButton}
                      onClick={handleCopyJson}
                      title="Copy full API response as JSON"
                      type="button"
                    >
                      <i className={`fe icon-${(copiedJson) ? 'check' : 'copy'}`} />
                      {(copiedJson) ? 'Copied!' : 'Copy Response JSON'}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {isRunning && (
            <LoadingSpinner message="Executing script..." />
          )}
        </div>

        <div className={`box ${styles.footer}`}>
          <Button
            className={styles.curlButton}
            onClick={handleCopyCurl}
            theme="outline"
            title="Copy as cURL"
            type="button"
          >
            <i className={`fe icon-${(copiedCurl) ? 'check' : 'copy'}`} style={{ marginRight: 4 }} />
            {(copiedCurl) ? 'Copied!' : 'cURL'}
          </Button>
          <div className={styles.footerActions}>
            <Button
              onClick={onClose}
              theme="outline"
              type="button"
            >
              Close
            </Button>
            <Button
              disabled={isRunning}
              onClick={handleRun}
              theme="default"
              type="button"
            >
              {(isRunning) ? 'Running...' : 'Run'}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

type VariableSectionProps = {
  icon: string;
  names: string[];
  onChange: (newValue: Record<string, string>) => void;
  title: string;
  values: Record<string, string>;
};

const VariableSection: FC<VariableSectionProps> = ({
  icon,
  names,
  onChange,
  title,
  values
}) => {
  return (!names.length) ? null : (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <i className={`fe ${icon} ${styles.sectionIcon}`} />
        {title}
      </div>
      <div className={styles.fieldGrid}>
        {names.map((name) => (
          <Fragment key={name}>
            <label className={styles.fieldLabel}>
              {name}
            </label>
            <input
              className={styles.fieldInput}
              onChange={(e) => onChange({ ...values, [name]: e.target.value })}
              placeholder="Enter value..."
              type="text"
              value={values[name] ?? ''}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
};
